import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '@/components/Breadcrumbs';
import Button from '@/components/Button';
import Tabs, { TTabItem, TabPanel } from '@/components/Tabs';
import { useBreadcrumbs } from '@/features/home/hooks/useBreadcrumbs';
import { TransactionHistory } from '@/features/home';
import TransactionDetailsDrawer from '@/features/home/components/TransactionDetailsDrawer';
import ExportModal from '@/features/home/components/ExportModal';
import {
  TransactionHistoryTabIcon,
  MonthlyStatementsTabIcon,
  DocumentsTabIcon,
  CopyIcon,
  CheckIcon,
  InformationCircle,
  ArrowUpIcon,
  ArrowRightLeftIcon
} from '@/components/Icons';
import MonthlyStatements from './components/MonthlyStatements';
import ProofOfAccount from './components/ProofOfAccount';
import useHomeContext from '@/hooks/useHomeContext';
import { TWalletData } from '@/features/home/types/WalletData';
import CurrencyFormat from 'react-currency-format';
import { getWalletTypeDisplay } from '@/utils/getWalletTypeDisplay';
import { WalletTypeEnum } from '@/context/types/Wallet';
import { routes } from '@/utils/routes';
import { Feature } from '@/config/featureFlags.definition';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { isWalletTypeEligible, WalletEligibilityStateEnum } from '@/utils/walletEligibility';

type TTab = 'transaction-history' | 'monthly-statements' | 'proof-of-account';

interface SelectedAccountProps {
  onSelectBankAccount: (selected?: TWalletData) => void;
  showBreadcrumbs?: boolean;
}

const tabs: TTabItem<TTab>[] = [
  { id: 'transaction-history', label: 'Transaction history', Icon: TransactionHistoryTabIcon },
  { id: 'monthly-statements', label: 'Monthly statements', Icon: MonthlyStatementsTabIcon },
  { id: 'proof-of-account', label: 'Proof of account', Icon: DocumentsTabIcon }
];

const SelectedAccount: React.FC<SelectedAccountProps> = ({
  onSelectBankAccount,
  showBreadcrumbs = true
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TTab>('transaction-history');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isAccountNumberCopied, setIsAccountNumberCopied] = useState(false);
  const { bankAccount } = useHomeContext()!;
  const { homeBreadcrumb } = useBreadcrumbs();

  const canPay = useMemo(
    () =>
      isFeatureEnabled(Feature.PAYMENT) &&
      isWalletTypeEligible(
        bankAccount?.walletType as WalletTypeEnum,
        WalletEligibilityStateEnum.PAY
      ),
    [bankAccount]
  );

  const canTransfer = useMemo(
    () =>
      isWalletTypeEligible(
        bankAccount?.walletType as WalletTypeEnum,
        WalletEligibilityStateEnum.TRANSFER
      ),
    [bankAccount]
  );

  const handleCopyAccountNumber = async () => {
    if (!bankAccount?.accountNumber) return;
    try {
      await navigator.clipboard.writeText(bankAccount.accountNumber);
      setIsAccountNumberCopied(true);
      setTimeout(() => setIsAccountNumberCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <>
      <TransactionDetailsDrawer />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen((prev) => !prev)}
      />

      {/* Account header */}
      <div className="px-4 pt-6 pb-9 bg-aux-lightgray relative flex flex-col gap-y-6">
        {showBreadcrumbs && <Breadcrumbs paths={homeBreadcrumb} />}

        <div className="w-full max-w-[600px] flex flex-col gap-y-6 p-4 rounded-[20px] border border-emerge-navy-4 bg-white">
          <div className="flex items-start justify-between gap-x-4">
            <span className="text-sm font-medium leading-5 text-emerge-navy">
              {bankAccount?.name}
            </span>

            {(canPay || canTransfer) && (
              <div className="flex items-center gap-x-2 flex-shrink-0">
                {canPay && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      navigate(routes.payment, { state: { preSelectedWallet: bankAccount } })
                    }>
                    <ArrowUpIcon size="xs" variant="inherit" />
                    Pay
                  </Button>
                )}
                {canTransfer && (
                  <Button
                    variant="primary_inverted"
                    size="sm"
                    onClick={() =>
                      navigate(routes.transferMoney, { state: { preSelectedWallet: bankAccount } })
                    }>
                    <ArrowRightLeftIcon size="xs" variant="inherit" />
                    Transfer
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-emerge-navy-2">Account balance</span>
              <CurrencyFormat
                className="text-emerge-navy text-[42px] font-medium leading-tight"
                value={Number(bankAccount?.balance ?? 0).toFixed(2)}
                displayType="text"
                thousandSeparator
                prefix="$"
                decimalScale={2}
              />
            </div>

            <div className="flex items-start gap-x-5">
              <div className="flex flex-col gap-y-0.5 py-1">
                <span className="text-xs font-medium text-emerge-navy-2">Account type</span>
                <span className="text-sm font-medium text-emerge-navy leading-5">
                  {getWalletTypeDisplay(bankAccount?.walletType as WalletTypeEnum)}
                </span>
              </div>

              {bankAccount?.walletType !== WalletTypeEnum.INVESTMENT && (
                <div className="flex-1 flex flex-col gap-y-0.5 py-1">
                  <span className="text-xs font-medium text-emerge-navy-2">Account number</span>
                  <div className="flex items-center gap-x-1">
                    <span className="text-sm font-medium text-dark-navy-30 leading-5">
                      {bankAccount?.accountNumber}
                    </span>
                    {bankAccount?.walletType === WalletTypeEnum.GLOBAL ? (
                      <InformationCircle design="filled" color="green" size="sm" />
                    ) : isAccountNumberCopied ? (
                      <CheckIcon size="sm" />
                    ) : (
                      <CopyIcon
                        size="sm"
                        className="cursor-pointer"
                        onClick={handleCopyAccountNumber}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-aux-lightgray w-full">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* Tab content */}
      <TabPanel id="transaction-history" activeTab={activeTab}>
        <div className="flex flex-col gap-y-4 bg-white">
          <TransactionHistory
            onSelectBankAccount={onSelectBankAccount}
            onOpenExport={() => setIsExportModalOpen(true)}
            alwaysShowFilters
          />
        </div>
      </TabPanel>

      {bankAccount?.id && (
        <TabPanel id="monthly-statements" activeTab={activeTab}>
          <MonthlyStatements walletId={bankAccount.id} accountName={bankAccount.name} />
        </TabPanel>
      )}

      {bankAccount?.id && (
        <TabPanel id="proof-of-account" activeTab={activeTab}>
          <ProofOfAccount walletId={bankAccount.id} />
        </TabPanel>
      )}
    </>
  );
};

export default SelectedAccount;
